import { useRecoilCallback } from 'recoil';
import { unsavedClinicalStepEdits } from '../state';
import { clearAllSteps, resetStepIndices } from '../clinicalStepStore';

/**
 * Clears every piece of stepped-HP state: the steps themselves, the last-saved baseline they are
 * compared against, the step and sub-step indices, and any pending revert snapshots.
 *
 * Deleting a hanging protocol used to leave this to each viewer window's own refetch-and-reinitialize
 * pass. That pass is skipped when a window observes "no HP" while its init guard is still set (the
 * guard exists so a transient null during a refetch does not blank the step chips), so a window that
 * had not yet processed the deletion broadcast kept rendering a step bar for an HP that no longer
 * exists, until the page was refreshed (VX-4738).
 *
 * Calling this from the delete path makes the deletion authoritative instead: `steppedHPConfigurations`
 * is BroadcastChannel-synced, so clearing it here clears the other viewer windows too, with no
 * dependence on refetch timing. The version bump from `makeVersionedSteps` is what lets the other
 * windows accept the cleared value. The baseline is no longer synced (it is a derived fact -- see
 * the atom), so each window clears its own; that is safe here because the clear is unconditional.
 *
 * Pass the hpKey of the protocol being cleared to also drop its persisted unsaved-edit record
 * (VX-4803). Backend smids are UUIDs so a recreated HP will not reuse the key, but leaving the record
 * behind strands a key the family's LRU only reclaims after 250 others.
 */
export function useClearSteppedHPState(): (hpKey?: Nullable<string>) => void {
  return useRecoilCallback(
    ({ set, reset }) =>
      (hpKey?: Nullable<string>) => {
        clearAllSteps(set);
        // `forHPKey: null` and not the hpKey being cleared. This is the teardown path -- the protocol
        // is gone -- so nobody owns the index any more. Attributing it to the departing HP would tell
        // the next init pass that the reader had positioned the index for it, and a recreated or
        // re-matched protocol would then skip its home.
        resetStepIndices(set, { forHPKey: null });
        // reset, not set-to-null, so the persister's reset branch removes the key instead of storing a
        // null under it.
        if (hpKey != null) reset(unsavedClinicalStepEdits(hpKey));
      },
    []
  );
}
